import React, { useState } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  useTheme,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import StarIcon from "@mui/icons-material/Star";
import { SystemSection, useSystemsContext } from "../../context/SystemsContext";
import { useSession } from "../../context/SessionContext";

const tabOptions: SystemSection[] = [
  "assetProcessing",
  "assetSyndication",
  "assetStorage",
  "clientProcess",
];

interface AccordionPanelProps {
  mobile?: boolean;
  onNavigate?: () => void;
}

const AccordionPanel: React.FC<AccordionPanelProps> = ({ mobile = false, onNavigate }) => {
  const theme = useTheme();
  const { user } = useSession();
  const isAdmin = user?.role === "Admin";

  const {
    configs,
    selectedConfig: expandedConfigId,
    selectedSection,
    selectConfig,
    setSelectedSection,
    createSystem,
    createConfig
  } = useSystemsContext();

  const [systemExpanded, setSystemExpanded] = useState(true);
  const [configExpanded, setConfigExpanded] = useState(true);

  return (
    <Box
      sx={{
        width: mobile ? "100%" : "300px",
        borderRight: mobile ? "none" : `1px solid ${theme.palette.divider}`,
        overflowY: "auto",
      }}
    >
      {/* 🔹 SYSTEM SECTION */}
      <Accordion
        expanded={systemExpanded}
        onChange={() => setSystemExpanded((prev) => !prev)}
        disableGutters
        square
        sx={{
          backgroundColor: theme.palette.background.paper,
          boxShadow: "none",
          "&:before": { display: "none" },
          m: 0,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            "& .MuiAccordionSummary-content": {
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%"
            },
          }}
        >
          <Typography variant="h6">System</Typography>

          {/* ➕ Add System Icon */}

          <IconButton
            data-cy="system-add-system-btn"
            size="small"
            onClick={(e) => {
              e.stopPropagation(); // prevent toggling accordion
              createSystem();
              onNavigate?.();
            }}
            sx={{
              ml: 1,
              color: theme.palette.status.accent,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0 }}>
          <List disablePadding>
            <ListItemButton
              data-cy="system-nav-system-info"
              selected={selectedSection === "systemInfo"}
              onClick={() => {
                selectConfig(null);
                onNavigate?.();
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {selectedSection === "systemInfo" && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.status.accent,
                          mr: 1,
                        }}
                      />
                    )}
                    <Typography variant="body2">System Info</Typography>
                  </Box>
                }
              />
            </ListItemButton>
            <ListItemButton
              data-cy="system-nav-system-hardware"
              selected={selectedSection === "systemHardware"}
              onClick={() => {
                selectConfig(null);
                setSelectedSection("systemHardware");
                onNavigate?.();
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {selectedSection === "systemHardware" && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.status.accent,
                          mr: 1,
                        }}
                      />
                    )}
                    <Typography variant="body2">System Hardware</Typography>
                  </Box>
                }
              />
            </ListItemButton>
            {isAdmin && (
              <ListItemButton
                data-cy="system-nav-service-definitions"
                selected={selectedSection === "serviceDefinitions"}
                onClick={() => {
                  setSelectedSection("serviceDefinitions");
                  onNavigate?.();
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {selectedSection === "serviceDefinitions" && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: theme.palette.status.accent,
                            mr: 1,
                          }}
                        />
                      )}
                      <Typography variant="body2">Service Definitions</Typography>
                    </Box>
                  }
                />
              </ListItemButton>
            )}
          </List>
        </AccordionDetails>
      </Accordion>

      {/* 🔹 CONFIGURATIONS SECTION */}
      <Accordion
        expanded={configExpanded}
        onChange={() => setConfigExpanded((prev) => !prev)}
        disableGutters
        square
        sx={{
          backgroundColor: theme.palette.background.paper,
          boxShadow: "none",
          "&:before": { display: "none" },
          m: 0,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            "& .MuiAccordionSummary-content": { display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" },
          }}
        >
          <Typography variant="h6">Configurations</Typography>

          {/* ➕ Add Config Icon (future) */}
          <IconButton
            data-cy="system-add-config-btn"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              createConfig();
              onNavigate?.();
            }}
            sx={{
              ml: 1,
              color: theme.palette.status.accent,
              "&:hover": { backgroundColor: theme.palette.action.hover },
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0 }}>
          {configs.map((cfg: any) => {
            const isDefault = cfg.clientProcess?.is_default || cfg.is_default;
            const configKey = cfg.configKey || cfg.name;
            const displayName = cfg.clientProcess?.name || cfg.name || configKey;

            return (
              <Accordion
                data-cy={`system-nav-config-${configKey}`}
                key={configKey}
                expanded={expandedConfigId === configKey}
                onChange={() => selectConfig(configKey)}
                disableGutters
                square
                sx={{
                  backgroundColor:
                    expandedConfigId === configKey
                      ? theme.palette.action.hover
                      : "inherit",
                  boxShadow: "none",
                  "&:before": { display: "none" },
                  borderLeft:
                    expandedConfigId === configKey
                      ? `2px solid ${theme.palette.status.accent}`
                      : "none",
                  m: 0,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    "& .MuiAccordionSummary-content": {
                      minWidth: 0, // 🔥 this is the missing piece
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      minWidth: 0, // 🔑 allows ellipsis inside flex
                      pr: 1,       // little space before expand icon
                    }}
                  >
                    <Tooltip title={displayName} arrow placement="top">
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1,
                          minWidth: 0, // 🔑
                        }}
                      >
                        {displayName}
                      </Typography>
                    </Tooltip>

                    {isDefault && (
                      <StarIcon
                        sx={{ ml: 1, flexShrink: 0, color: theme.palette.status.accent }}
                        fontSize="inherit"
                      />
                    )}
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 0 }}>
                  <List disablePadding>
                    {tabOptions.map((section) => (
                      <ListItemButton
                        data-cy={`system-nav-config-section-${section}`}
                        key={section}
                        selected={
                          expandedConfigId === configKey &&
                          selectedSection === section
                        }
                        onClick={() => {
                          setSelectedSection(section);
                          onNavigate?.();
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              {expandedConfigId === configKey &&
                                selectedSection === section && (
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      backgroundColor: theme.palette.status.accent,
                                      mr: 1,
                                    }}
                                  />
                                )}
                              <Typography variant="body2">{section}</Typography>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            );
          })
          }
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default AccordionPanel;
